import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  Divider,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/slices/toastSlice";
import {
  CREATE_UPDATE_BUTTON,
  RESET_CANCEL_BUTTON,
} from "../../styles/ButtonStyles";
import {
  updateUserAddress,
  updateUserCompany,
  updateUserContact,
  updateUserGst,
  updateUserName,
  updateUserPassword,
} from "../../services/api/user";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export const UpdateUserDetails = ({
  open,
  onClose,
  fieldToEdit,
  fieldValue,
  userDetails,
}) => {
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleTogglePassword = () => setShowPassword((prev) => !prev);
  const handleToggleConfirmPassword = () =>
    setShowConfirmPassword((prev) => !prev);

  useEffect(() => {
    // Initialize form values dynamically based on what’s being edited
    switch (fieldToEdit) {
      case "Name":
        setFormValues({
          first_name: userDetails.first_name || "",
          last_name: userDetails.last_name || "",
        });
        break;

      case "Address":
        setFormValues({
          address: userDetails.address || "",
        });
        break;

      case "Phone Number":
        setFormValues({
          contact: userDetails.contact || "",
        });
        break;

      case "Company":
        setFormValues({
          company: userDetails.company || "",
        });
        break;

      case "GST Number":
        setFormValues({
          gst_no: userDetails.gst_no || "",
        });
        break;

      case "Change Password":
        setFormValues({
          new_password: "",
          confirm_password: "",
        });
        break;

      case "Email":
        setFormValues({
          email: userDetails.email || "",
        });
        break;

      default:
        setFormValues({
          value: fieldValue || "",
        });
    }
  }, [fieldToEdit, userDetails, fieldValue]);

  const handleChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveChanges = async (formValues) => {
    try {
      let response;

      switch (fieldToEdit) {
        case "Name":
          response = await updateUserName({
            first_name: formValues.first_name,
            last_name: formValues.last_name,
          });
          break;

        case "Address":
          response = await updateUserAddress({
            address: formValues.address,
          });
          break;

        case "Phone Number":
          response = await updateUserContact({
            contact: formValues.contact,
          });
          break;

        case "Company":
          response = await updateUserCompany({
            company: formValues.company,
          });
          break;

        case "GST Number":
          response = await updateUserGst({
            gst_no: formValues.gst_no,
          });
          break;

        case "Change Password":
          response = await updateUserPassword({
            new_password: formValues.new_password,
            confirm_password: formValues.confirm_password,
          });
          break;

        default:
          response = await updateUserGeneric(formValues);
      }

      dispatch(showToast({ message: response.message, severity: "success" }));

      // Close the model and refresh user details after successful update
      onClose;
      window.location.reload();
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ backgroundColor: "#ffedbb" }}>
        Edit {fieldToEdit}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 3, mb: 3 }}>
          {/* Conditional Rendering based on fieldToEdit */}
          {fieldToEdit === "Name" && (
            <>
              <TextField
                label="First Name"
                value={formValues.first_name || ""}
                onChange={(e) => handleChange("first_name", e.target.value)}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={formValues.last_name || ""}
                onChange={(e) => handleChange("last_name", e.target.value)}
                fullWidth
              />
            </>
          )}

          {fieldToEdit === "Address" && (
            <TextField
              label="Address"
              value={formValues.address || ""}
              onChange={(e) => handleChange("address", e.target.value)}
              fullWidth
            />
          )}

          {fieldToEdit === "Phone Number" && (
            <TextField
              label="Phone Number"
              value={formValues.contact || ""}
              onChange={(e) => handleChange("contact", e.target.value)}
              fullWidth
            />
          )}

          {fieldToEdit === "Company" && (
            <TextField
              label="Company"
              value={formValues.company || ""}
              onChange={(e) => handleChange("company", e.target.value)}
              fullWidth
            />
          )}

          {fieldToEdit === "GST Number" && (
            <TextField
              label="GST Number"
              value={formValues.gst_no || ""}
              onChange={(e) => handleChange("gst_no", e.target.value)}
              fullWidth
            />
          )}

          {fieldToEdit === "Change Password" && (
            <>
              <TextField
                label="New Password"
                type={showPassword ? "text" : "password"}
                value={formValues.new_password || ""}
                onChange={(e) => handleChange("new_password", e.target.value)}
                fullWidth
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                value={formValues.confirm_password || ""}
                onChange={(e) =>
                  handleChange("confirm_password", e.target.value)
                }
                fullWidth
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ backgroundColor: "#f9f9f9" }}>
        <Button onClick={onClose} sx={RESET_CANCEL_BUTTON}>
          Cancel
        </Button>
        <Button
          onClick={() => handleSaveChanges(formValues)}
          variant="contained"
          sx={{ CREATE_UPDATE_BUTTON }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};
